import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { loginByPassword, registerAccount, sendVerificationCode, checkInviteCode } from '@/api/auth';
import { ApiRequestError } from '@/api/types';
import ForgotPasswordModal from './components/ForgotPasswordModal';
import { getPublicSettings } from '@/api/settings';

// ── 格式校验辅助函数 ──────────────────────────────────────────────────
const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const CN_MOBILE_PHONE_REGEX = /^(?:\+?86)?1[3-9]\d{9}$/;

function getPasswordStrength(pwd: string): { score: number; label: string; color: string } {
  if (!pwd) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pwd.length >= 8) score++;
  if (pwd.length >= 12) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  if (score <= 1) return { score, label: '弱', color: '#ef4444' };
  if (score <= 3) return { score, label: '中', color: '#f97316' };
  return { score, label: '强', color: '#10b981' };
}

// ── 字段级错误类型声明 ────────────────────────────────────────────────
interface LoginErrors { email?: string; password?: string }
interface RegisterErrors { name?: string; phone?: string; email?: string; password?: string; confirm?: string; company?: string }

// ── 通用输入框组件 ──────────────────────────────────────────────────
interface InputFieldProps {
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  error?: string;
  success?: boolean;
  hint?: string;
  required?: boolean;
  rightSlot?: React.ReactNode;
  onBlur?: () => void;
}

function InputField({ label, type = 'text', value, onChange, placeholder, error, success, hint, required, rightSlot, onBlur }: InputFieldProps) {
  const borderClass = error
    ? 'border-red-300 focus:border-red-400'
    : success
    ? 'border-[#10b981] focus:border-[#10b981]'
    : 'border-gray-200 focus:border-[#0ea5e9]';

  return (
    <div>
      {label && (
        <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1.5">
          {label}
          {required && <span className="text-red-400 text-xs">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          className={`w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none transition-colors pr-${rightSlot ? '10' : '4'} ${borderClass}`}
        />
        {rightSlot && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">{rightSlot}</div>
        )}
        {!rightSlot && success && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <i className="ri-checkbox-circle-fill text-[#10b981] text-base"></i>
          </div>
        )}
        {!rightSlot && error && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <i className="ri-error-warning-fill text-red-400 text-base"></i>
          </div>
        )}
      </div>
      {error && (
        <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
          <i className="ri-error-warning-line"></i>{error}
        </p>
      )}
      {!error && hint && (
        <p className="mt-1.5 text-xs text-gray-400">{hint}</p>
      )}
    </div>
  );
}

// ── 密码强度指示条 ──────────────────────────────────────────────────
function PasswordStrengthBar({ password }: { password: string }) {
  const { score, label, color } = getPasswordStrength(password);
  if (!password) return null;
  return (
    <div className="mt-2">
      <div className="flex gap-1 mb-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="flex-1 h-1 rounded-full transition-all duration-300"
            style={{ backgroundColor: i <= score ? color : '#e5e7eb' }}
          ></div>
        ))}
      </div>
      <p className="text-xs" style={{ color }}>
        密码强度：{label}
        {score <= 1 && ' — 建议加入大写字母、数字或符号'}
        {score === 2 && ' — 再加点特殊字符会更安全'}
        {score >= 4 && ' — 很棒，密码很安全！'}
      </p>
    </div>
  );
}

// ── 用户协议 / 隐私政策弹窗 ──────────────────────────────────────────
type DocType = 'terms' | 'privacy';
function DocModal({ type, onClose }: { type: DocType; onClose: () => void }) {
  const isTerms = type === 'terms';
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[400] p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h3 className="font-bold text-gray-900">{isTerms ? '用户协议' : '隐私政策'}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500"></i>
          </button>
        </div>
        <div className="overflow-y-auto p-6 text-sm text-gray-600 space-y-4 leading-relaxed">
          {isTerms ? (
            <>
              <p className="text-xs text-gray-400">最后更新：2026年01月01日</p>
              <h4 className="font-semibold text-gray-800">一、服务说明</h4>
              <p>万象杂货铺（以下简称"本平台"）是一个专注于企业信息化平替方案的知识分享平台。注册成为会员即表示您同意本协议的全部条款。</p>
              <h4 className="font-semibold text-gray-800">二、账号注册与管理</h4>
              <p>1. 您需提供真实、准确的注册信息，并负责保管账号安全。</p>
              <p>2. 禁止将账号出借、转让或共享给他人使用。</p>
              <p>3. 如发现账号被盗用，请立即联系平台客服。</p>
              <h4 className="font-semibold text-gray-800">三、内容使用规范</h4>
              <p>1. 平台内容仅供个人学习、研究及企业内部参考使用。</p>
              <p>2. 未经授权，禁止对平台内容进行商业转载、售卖或二次传播。</p>
              <p>3. 平台方案仅供参考，实际部署请结合自身业务情况评估。</p>
              <h4 className="font-semibold text-gray-800">四、免责声明</h4>
              <p>本平台分享的开源方案均来源于公开资源，平台不对实施结果承担法律责任。使用前请充分评估技术风险。</p>
              <h4 className="font-semibold text-gray-800">五、协议变更</h4>
              <p>本平台保留随时修改本协议的权利，修改后将在平台公告，继续使用即视为同意新协议。</p>
            </>
          ) : (
            <>
              <p className="text-xs text-gray-400">最后更新：2026年01月01日</p>
              <h4 className="font-semibold text-gray-800">一、信息收集</h4>
              <p>我们收集的信息包括：注册时提供的姓名、邮箱、公司等基本信息；您在平台上的浏览、收藏、下载等行为数据。</p>
              <h4 className="font-semibold text-gray-800">二、信息使用</h4>
              <p>1. 提供平台核心服务，包括方案推荐、下载记录管理等。</p>
              <p>2. 发送您订阅的通知邮件（如新方案发布、工具更新）。</p>
              <p>3. 改善平台产品体验和内容质量。</p>
              <h4 className="font-semibold text-gray-800">三、信息保护</h4>
              <p>您的密码经过加密存储，我们不会将您的个人信息出售给任何第三方。平台采用行业标准安全措施保护您的数据。</p>
              <h4 className="font-semibold text-gray-800">四、Cookie 使用</h4>
              <p>本平台使用 sessionStorage 保存登录状态，关闭浏览器后会自动失效；“记住账号”仅保存邮箱输入，不会保持登录。</p>
              <h4 className="font-semibold text-gray-800">五、您的权利</h4>
              <p>您有权查看、更正或删除您的个人信息。注销账号后，我们将在30天内删除您的全部数据。</p>
              <h4 className="font-semibold text-gray-800">六、联系我们</h4>
              <p>如有隐私相关问题，请通过平台内的联系方式与我们取得联系。</p>
            </>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-100 shrink-0">
          <button
            onClick={onClose}
            className="w-full py-2.5 bg-[#0ea5e9] text-white font-semibold rounded-xl hover:bg-[#0284c7] transition-all cursor-pointer whitespace-nowrap"
          >
            我已阅读
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 姓名/道号敏感词库 ───────────────────────────────────────────────
const FORBIDDEN_WORDS_REG = ['管理员', '超级管理员', 'admin', 'administrator', 'superadmin', '管理', '系统', 'system', 'root'];
function containsForbiddenWordReg(str: string): boolean {
  const lower = str.toLowerCase().replace(/\s/g, '');
  return FORBIDDEN_WORDS_REG.some((w) => lower.includes(w.toLowerCase()));
}

function resolveAuthErrorMessage(error: unknown, mode: 'login' | 'register'): string {
  if (error instanceof ApiRequestError) {
    if (error.message) return error.message;
    if (mode === 'register' && error.code === 40010) return '该邮箱已被注册，请直接登录';
    if (mode === 'login' && error.code === 40101) return '账号或密码错误，请重试';
    if (mode === 'login' && error.code === 40310) return '账号状态异常，暂不可登录';
    if (error.code === 42200) return '提交信息不完整或格式不正确';
    if (error.code === 42900) return '请求过于频繁，请稍后再试';
    if (error.code >= 50000) return '服务暂时不可用，请稍后重试';
  }
  return mode === 'login' ? '登录失败，请稍后重试' : '注册失败，请稍后重试';
}

// ── 登录与注册主页面组件 ──────────────────────────────────────────────
export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setSession, isLoggedIn, isReady } = useAuth();

  const [allowRegister, setAllowRegister] = useState(false);
  const [features, setFeatures] = useState<Record<string, boolean> | null>(null);
  const [siteName, setSiteName] = useState('');
  const [logoPreview, setLogoPreview] = useState('');
  const [inviteRewardInviter, setInviteRewardInviter] = useState<number | null>(null);
  const [inviteRewardInvitee, setInviteRewardInvitee] = useState<number | null>(null);

  useEffect(() => {
    getPublicSettings()
      .then((res) => {
        setAllowRegister(res.allowRegister);
        if ((res as any).siteName) setSiteName((res as any).siteName);
        if ((res as any).logoPreview) setLogoPreview((res as any).logoPreview);
        const inviterReward = Number(res.pointsPolicy?.inviteInviterRewardPoints);
        const inviteeReward = Number(res.pointsPolicy?.inviteInviteeRewardPoints);
        setInviteRewardInviter(Number.isFinite(inviterReward) ? inviterReward : null);
        setInviteRewardInvitee(Number.isFinite(inviteeReward) ? inviteeReward : null);
        if (!res.features || typeof res.features.invite !== 'boolean') {
          throw new Error('功能开关响应格式错误');
        }
        setFeatures(res.features);
        window.dispatchEvent(new CustomEvent('wanxiang_settings_updated'));
        window.dispatchEvent(new CustomEvent('wanxiang_feature_updated'));
      })
      .catch((err) => {
        console.error('Failed to fetch public settings:', err);
      });
  }, []);

  const isAllowRegister = useCallback(() => allowRegister, [allowRegister]);

  const [tab, setTab] = useState<'login' | 'register'>(() => {
    const t = searchParams.get('tab') === 'register' ? 'register' : 'login';
    if (t === 'register' && !isAllowRegister()) {
      return 'login';
    }
    return t;
  });

  useEffect(() => {
    if (tab === 'register' && !isAllowRegister()) {
      setTab('login');
    }
  }, [tab, isAllowRegister]);

  useEffect(() => {
    if (isReady && isLoggedIn) {
      navigate('/', { replace: true });
    }
  }, [isReady, isLoggedIn, navigate]);

  // 登录表单状态管理
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginErrors, setLoginErrors] = useState<LoginErrors>({});
  const [loginTouched, setLoginTouched] = useState<Record<string, boolean>>({});
  const [showLoginPwd, setShowLoginPwd] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [loginGlobalError, setLoginGlobalError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  // 加载记住的账号
  useEffect(() => {
    const savedAccount = localStorage.getItem('wanxiang_remember_account');
    if (savedAccount) {
      setLoginForm((prev) => ({ ...prev, email: savedAccount }));
      setRememberMe(true);
    }
  }, []);

  // 注册表单状态管理
  const [regForm, setRegForm] = useState({
    name: '', phone: '', email: '', password: '', confirm: '', company: '',
    inviteCode: searchParams.get('ref') || '',
  });
  const [regErrors, setRegErrors] = useState<RegisterErrors>({});
  const [regTouched, setRegTouched] = useState<Record<string, boolean>>({});
  const [showRegPwd, setShowRegPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [regLoading, setRegLoading] = useState(false);
  const [regSuccess, setRegSuccess] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeError, setAgreeError] = useState('');

  // 注册邮箱验证码相关状态
  const [regCode, setRegCode] = useState('');
  const [regCodeSent, setRegCodeSent] = useState(false);
  const [regCodeCountdown, setRegCodeCountdown] = useState(0);
  const [regCodeError, setRegCodeError] = useState('');
  const [regEmailSending, setRegEmailSending] = useState(false);

  // 邀请码校验状态
  const [inviteCodeStatus, setInviteCodeStatus] = useState<'idle' | 'checking' | 'valid' | 'invalid'>('idle');

  // 协议/隐私文档弹窗显示状态
  const [docModal, setDocModal] = useState<DocType | null>(null);

  // ── 登录字段基本校验 ──
  const validateLogin = useCallback((): LoginErrors => {
    const errs: LoginErrors = {};
    if (!loginForm.email.trim()) errs.email = '请输入手机号或邮箱';
    if (!loginForm.password) errs.password = '请输入密码';
    else if (loginForm.password.length < 6) errs.password = '密码至少 6 位';
    return errs;
  }, [loginForm]);

  // ── 注册字段详细校验 ──
  const validateRegister = useCallback((): RegisterErrors => {
    const errs: RegisterErrors = {};
    if (!regForm.name.trim()) errs.name = '请输入姓名';
    else if (regForm.name.trim().length < 2) errs.name = '姓名至少 2 个字符';
    else if (containsForbiddenWordReg(regForm.name)) errs.name = '姓名包含敏感词，请更换';
    if (!regForm.phone.trim()) errs.phone = '请输入手机号';
    else if (!CN_MOBILE_PHONE_REGEX.test(regForm.phone.trim())) errs.phone = '手机号格式不正确';
    if (!regForm.email) errs.email = '请输入邮箱地址';
    else if (!isValidEmail(regForm.email)) errs.email = '邮箱格式不正确';
    if (!regForm.password) errs.password = '请设置密码';
    else if (regForm.password.length < 8) errs.password = '密码至少 8 位';
    else if (!/[A-Za-z]/.test(regForm.password) || !/[0-9]/.test(regForm.password)) errs.password = '密码需包含字母和数字';
    if (!regForm.confirm) errs.confirm = '请确认密码';
    else if (regForm.confirm !== regForm.password) errs.confirm = '两次密码不一致';
    return errs;
  }, [regForm]);

  // ── 发送注册邮箱验证码 ──
  const handleSendRegCode = async () => {
    if (!isValidEmail(regForm.email)) {
      setRegTouched((p) => ({ ...p, email: true }));
      setRegErrors((prev) => ({ ...prev, email: '邮箱格式不正确' }));
      return;
    }
    setRegErrors((prev) => ({ ...prev, email: undefined }));
    setRegEmailSending(true);
    setRegCodeError('');
    try {
      await sendVerificationCode({ email: regForm.email.trim().toLowerCase(), purpose: 'register' });
      setRegCodeSent(true);
      setRegCodeCountdown(60);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        if (err.code === 42900) {
          const retryAfter = (err as any).details?.retryAfter as number | undefined;
          setRegCodeError(retryAfter ? `操作频繁，请 ${retryAfter} 秒后再试` : '发送过于频繁，请稍后重试');
        } else {
          setRegCodeError(err.message || '发送失败，请稍后重试');
        }
      } else {
        setRegCodeError('邮件发送失败，请检查邮箱地址后重试');
      }
    } finally {
      setRegEmailSending(false);
    }
  };

  // 注册验证码倒计时副作用
  useEffect(() => {
    if (regCodeCountdown <= 0) return;
    const t = setTimeout(() => setRegCodeCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [regCodeCountdown]);

  // 登录表单字段触摸后的实时失焦校验
  useEffect(() => {
    if (Object.keys(loginTouched).length > 0) {
      const errs = validateLogin();
      const filtered: LoginErrors = {};
      if (loginTouched.email) filtered.email = errs.email;
      if (loginTouched.password) filtered.password = errs.password;
      setLoginErrors(filtered);
    }
  }, [loginForm, loginTouched, validateLogin]);

  useEffect(() => {
    if (Object.keys(regTouched).length > 0) {
      const errs = validateRegister();
      const filtered: RegisterErrors = {};
      if (regTouched.name) filtered.name = errs.name;
      if (regTouched.phone) filtered.phone = errs.phone;
      if (regTouched.email) filtered.email = errs.email;
      if (regTouched.password) filtered.password = errs.password;
      if (regTouched.confirm) filtered.confirm = errs.confirm;
      setRegErrors(filtered);
    }
  }, [regForm, regTouched, validateRegister]);

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const allTouched = { email: true, password: true };
    setLoginTouched(allTouched);
    const errs = validateLogin();
    setLoginErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setLoginLoading(true);
    setLoginGlobalError('');
    try {
      const authPayload = await loginByPassword({
        account: loginForm.email.trim(),
        password: loginForm.password,
      });
      setSession(authPayload);
      
      // 保存或清除被记住的账号
      if (rememberMe) {
        localStorage.setItem('wanxiang_remember_account', loginForm.email.trim());
      } else {
        localStorage.removeItem('wanxiang_remember_account');
      }

      setLoginSuccess(true);
      setTimeout(() => navigate('/'), 1000);
    } catch (error) {
      setLoginGlobalError(resolveAuthErrorMessage(error, 'login'));
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const allTouched = { name: true, phone: true, email: true, password: true, confirm: true };
    setRegTouched(allTouched);
    const errs = validateRegister();
    setRegErrors(errs);
    if (!agreeTerms) { setAgreeError('请先同意用户协议'); return; }
    setAgreeError('');
    if (Object.keys(errs).length > 0) return;

    // 基础前端校验：确保已发送验证码并输入了 6 位
    if (!regCodeSent) {
      setRegCodeError('请先获取邮箱验证码');
      return;
    }
    if (!regCode || regCode.length < 6) {
      setRegCodeError('请输入 6 位验证码');
      return;
    }
    setRegCodeError('');

    setRegLoading(true);
    try {
      const isInviteEnabled = () => features?.invite === true;
      const authPayload = await registerAccount({
        email: regForm.email.trim().toLowerCase(),
        password: regForm.password,
        name: regForm.name.trim(),
        phone: regForm.phone.trim(),
        code: regCode,
        inviteCode: isInviteEnabled() && regForm.inviteCode.trim().toUpperCase() ? regForm.inviteCode.trim().toUpperCase() : undefined,
        company: regForm.company.trim() || undefined,
      });
      setSession(authPayload);
      setRegSuccess(true);
      setTimeout(() => navigate('/'), 1500);
    } catch (error) {
      const msg = resolveAuthErrorMessage(error, 'register');
      // 验证码相关错误显示在验证码输入框下方
      if (error instanceof ApiRequestError && error.message.includes('验证码')) {
        setRegCodeError(msg);
      } else {
        setRegErrors((prev) => ({ ...prev, email: msg }));
      }
    } finally {
      setRegLoading(false);
    }
  };

  const switchTab = (t: 'login' | 'register') => {
    setTab(t);
    setLoginErrors({});
    setRegErrors({});
    setLoginTouched({});
    setRegTouched({});
    setLoginGlobalError('');
    setAgreeError('');
    setLoginSuccess(false);
    setRegSuccess(false);
    setInviteCodeStatus('idle');
  };

  const pwdStrength = getPasswordStrength(regForm.password);
  const inviteRewardInviterText = inviteRewardInviter ?? '--';
  const inviteRewardInviteeText = inviteRewardInvitee ?? '--';

  return (
    <div className="h-screen flex overflow-hidden">
      {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}
      {docModal && <DocModal type={docModal} onClose={() => setDocModal(null)} />}

      {/* 左侧装饰性品牌面板 */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=abstract%20technology%20digital%20network%20futuristic%20dark%20blue%20background%20with%20glowing%20particles%20and%20circuit%20patterns%2C%20deep%20space%20atmosphere%2C%20high%20contrast%2C%20professional&width=960&height=1080&seq=login-bg-01&orientation=portrait"
          alt="background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f172a]/80 via-[#0ea5e9]/30 to-[#0f172a]/70"></div>
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-2.5 cursor-pointer">
            <div className="w-9 h-9 flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 overflow-hidden shrink-0">
              {logoPreview ? (
                <img src={logoPreview} alt="logo" className="w-full h-full object-cover" />
              ) : (
                <i className="ri-store-2-line text-white text-base"></i>
              )}
            </div>
            <span className="font-bold text-white text-lg">{siteName}</span>
          </Link>

          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 text-white/70 text-xs mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#f97316] animate-pulse"></span>
              企业信息化平替方案
            </div>
            <h2 className="text-4xl font-bold text-white leading-tight mb-4">
              邪修之路<br />从这里开始
            </h2>
            <p className="text-white/60 text-base leading-relaxed mb-8">
              加入{siteName}，获取精选平替方案，<br />
              降低 80%+ 企业信息化成本。
            </p>
            <div className="grid grid-cols-3 gap-4">
              {[
                { value: '20+', label: '开源方案' },
                { value: '80%', label: '成本节省' },
                { value: '1000+', label: '企业使用' },
              ].map((s) => (
                <div key={s.label} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-3 text-center">
                  <div className="text-xl font-bold text-[#0ea5e9]">{s.value}</div>
                  <div className="text-white/50 text-xs mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {[
              { icon: 'ri-shield-check-line', text: '数据安全' },
              { icon: 'ri-open-source-line', text: '完全开源' },
              { icon: 'ri-community-line', text: '社区支持' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-1.5 text-white/40 text-xs">
                <i className={`${item.icon} text-white/30`}></i>
                {item.text}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 右侧登录表单面板 */}
      <div className="flex-1 bg-[#f8fafc] overflow-y-auto h-full">
        <div className="min-h-full flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-md">
          {/* 移动端 Logo 展示 */}
          <div className="lg:hidden text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 cursor-pointer">
              <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-[#0ea5e9] to-[#0284c7] rounded-xl overflow-hidden shrink-0">
                {logoPreview ? (
                  <img src={logoPreview} alt="logo" className="w-full h-full object-cover" />
                ) : (
                  <i className="ri-store-2-line text-white"></i>
                )}
              </div>
              <span className="font-bold text-xl text-gray-900">{siteName}</span>
            </Link>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-8">
            {/* 登录/注册选项卡切换 */}
            {isAllowRegister() && (
              <div className="flex gap-1 bg-gray-50 rounded-xl p-1 mb-7">
                {(['login', 'register'] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => switchTab(t)}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      tab === t ? 'bg-white text-gray-900' : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    {t === 'login' ? '登录账号' : '免费注册'}
                  </button>
                ))}
              </div>
            )}

            {/* ── 登录表单区域 ── */}
            {tab === 'login' && (
              <>
                {loginSuccess && (
                  <div className="mb-5 p-4 bg-[#10b981]/10 border border-[#10b981]/20 rounded-xl flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#10b981] rounded-full shrink-0">
                      <i className="ri-checkbox-circle-line text-white text-base"></i>
                    </div>
                    <div>
                      <p className="text-[#10b981] font-semibold text-sm">登录成功！</p>
                      <p className="text-[#10b981]/70 text-xs">正在跳转到首页...</p>
                    </div>
                  </div>
                )}

                {loginGlobalError && (
                  <div className="mb-5 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-full shrink-0 mt-0.5">
                      <i className="ri-error-warning-line text-red-500 text-base"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-red-600 text-sm font-medium">{loginGlobalError}</p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleLoginSubmit} className="space-y-4" noValidate>
                  <InputField
                    label="手机号 / 邮箱"
                    type="text"
                    value={loginForm.email}
                    onChange={(v) => setLoginForm({ ...loginForm, email: v })}
                    onBlur={() => setLoginTouched((p) => ({ ...p, email: true }))}
                    placeholder="请输入手机号或邮箱"
                    error={loginErrors.email}
                    success={loginTouched.email && !loginErrors.email && !!loginForm.email}
                    required
                  />

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="flex items-center gap-1 text-sm font-medium text-gray-700">
                        密码<span className="text-red-400 text-xs">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowForgot(true)}
                        className="text-xs text-[#0ea5e9] hover:text-[#0284c7] cursor-pointer whitespace-nowrap"
                      >
                        忘记密码？
                      </button>
                    </div>
                    <InputField
                      label=""
                      type={showLoginPwd ? 'text' : 'password'}
                      value={loginForm.password}
                      onChange={(v) => setLoginForm({ ...loginForm, password: v })}
                      onBlur={() => setLoginTouched((p) => ({ ...p, password: true }))}
                      placeholder="请输入密码"
                      error={loginErrors.password}
                      success={loginTouched.password && !loginErrors.password && !!loginForm.password}
                      rightSlot={
                        <button
                          type="button"
                          onClick={() => setShowLoginPwd((v) => !v)}
                          className="cursor-pointer text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <i className={`${showLoginPwd ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`}></i>
                        </button>
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <div
                        onClick={() => setRememberMe((v) => !v)}
                        className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all cursor-pointer ${
                          rememberMe ? 'bg-[#0ea5e9] border-[#0ea5e9]' : 'border-gray-300'
                        }`}
                      >
                        {rememberMe && <i className="ri-check-line text-white text-xs"></i>}
                      </div>
                      <span className="text-sm text-gray-600">记住我</span>
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={loginLoading || loginSuccess}
                    className="w-full py-3 bg-[#0ea5e9] text-white font-semibold rounded-xl hover:bg-[#0284c7] transition-all cursor-pointer whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loginLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        登录中...
                      </>
                    ) : loginSuccess ? (
                      <>
                        <i className="ri-checkbox-circle-line"></i>
                        登录成功
                      </>
                    ) : '登录'}
                  </button>
                </form>

                {isAllowRegister() && (
                  <p className="text-center text-sm text-gray-500 mt-5">
                    还没有账号？
                    <button onClick={() => switchTab('register')} className="text-[#0ea5e9] font-semibold cursor-pointer ml-1">
                      免费注册
                    </button>
                  </p>
                )}
              </>
            )}

            {/* ── 注册表单区域 ── */}
            {tab === 'register' && (
              <>
                {regSuccess && (
                  <div className="mb-5 p-4 bg-[#10b981]/10 border border-[#10b981]/20 rounded-xl flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#10b981] rounded-full shrink-0">
                      <i className="ri-checkbox-circle-line text-white text-base"></i>
                    </div>
                    <div>
                      <p className="text-[#10b981] font-semibold text-sm">注册成功！欢迎加入！</p>
                      <p className="text-[#10b981]/70 text-xs">正在跳转到首页...</p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleRegisterSubmit} className="space-y-4" noValidate>
                  <div className="grid grid-cols-2 gap-3">
                    <InputField
                      label="姓名"
                      value={regForm.name}
                      onChange={(v) => setRegForm({ ...regForm, name: v })}
                      onBlur={() => setRegTouched((p) => ({ ...p, name: true }))}
                      placeholder="请输入姓名"
                      error={regErrors.name}
                      success={regTouched.name && !regErrors.name && regForm.name.trim().length >= 2}
                      required
                    />
                    <InputField
                      label="手机号"
                      value={regForm.phone}
                      onChange={(v) => setRegForm({ ...regForm, phone: v })}
                      onBlur={() => setRegTouched((p) => ({ ...p, phone: true }))}
                      placeholder="请输入手机号"
                      error={regErrors.phone}
                      success={regTouched.phone && !regErrors.phone && !!regForm.phone.trim()}
                      hint={!regErrors.phone ? '注册后可使用手机号或邮箱登录' : undefined}
                      required
                    />
                  </div>

                  <InputField
                    label="公司名称"
                    value={regForm.company}
                    onChange={(v) => setRegForm({ ...regForm, company: v })}
                    placeholder="选填"
                    hint="选填，可后续补充"
                  />

                  {(() => {
                    const isInviteEnabled = () => features?.invite === true;
                    if (!isInviteEnabled()) return null;
                    return (
                      <div>
                        <InputField
                          label="邀请码"
                          value={regForm.inviteCode}
                          onChange={(v) => {
                            setRegForm({ ...regForm, inviteCode: v });
                            setInviteCodeStatus('idle');
                          }}
                          onBlur={async () => {
                            const code = regForm.inviteCode.trim().toUpperCase();
                            if (!code) { setInviteCodeStatus('idle'); return; }
                            setInviteCodeStatus('checking');
                            try {
                              const res = await checkInviteCode(code);
                              setInviteCodeStatus(res.valid ? 'valid' : 'invalid');
                            } catch {
                              setInviteCodeStatus('idle');
                            }
                          }}
                          placeholder={`填写好友邀请码可得 +${inviteRewardInviteeText} 灵石（选填）`}
                          success={inviteCodeStatus === 'valid'}
                          error={inviteCodeStatus === 'invalid' ? '邀请码无效，请确认后重试' : undefined}
                        />
                        {inviteCodeStatus === 'checking' && (
                          <p className="mt-1.5 text-xs text-gray-400 flex items-center gap-1">
                            <span className="w-3 h-3 border border-gray-300 border-t-[#0ea5e9] rounded-full animate-spin inline-block"></span>
                            校验中...
                          </p>
                        )}
                        {inviteCodeStatus === 'valid' && (
                          <p className="mt-1.5 text-xs text-[#10b981] flex items-center gap-1">
                            <i className="ri-checkbox-circle-line"></i>邀请码有效，注册成功后邀请人 +{inviteRewardInviterText}，被邀请人 +{inviteRewardInviteeText} 灵石
                          </p>
                        )}
                        {inviteCodeStatus === 'idle' && !regForm.inviteCode && (
                          <p className="mt-1.5 text-xs text-gray-400">填写后注册成功，邀请人 +{inviteRewardInviterText}，被邀请人 +{inviteRewardInviteeText} 灵石奖励</p>
                        )}
                      </div>
                    );
                  })()}

                  <InputField
                    label="邮箱地址"
                    type="email"
                    value={regForm.email}
                    onChange={(v) => { setRegForm({ ...regForm, email: v }); setRegCodeSent(false); setRegCode(''); setRegCodeError(''); }}
                    onBlur={() => setRegTouched((p) => ({ ...p, email: true }))}
                    placeholder="请输入邮箱地址"
                    error={regErrors.email}
                    success={regTouched.email && !regErrors.email && !!regForm.email}
                    required
                  />

                  {/* 邮箱验证码输入 */}
                  <div>
                    <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1.5">
                      邮箱验证码<span className="text-red-400 text-xs">*</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={regCode}
                        onChange={(e) => { setRegCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setRegCodeError(''); }}
                        placeholder="6位验证码"
                        disabled={!regCodeSent}
                        className={`flex-1 px-4 py-2.5 border rounded-xl text-sm focus:outline-none transition-colors ${
                          regCodeError ? 'border-red-300 focus:border-red-400' : 'border-gray-200 focus:border-[#0ea5e9]'
                        } ${!regCodeSent ? 'bg-gray-50 text-gray-400' : ''}`}
                      />
                      <button
                        type="button"
                        onClick={handleSendRegCode}
                        disabled={regEmailSending || regCodeCountdown > 0 || !isValidEmail(regForm.email)}
                        className="px-4 py-2.5 bg-[#0ea5e9] text-white text-sm font-medium rounded-xl hover:bg-[#0284c7] transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
                      >
                        {regEmailSending ? (
                          <span className="flex items-center gap-1.5">
                            <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            发送中
                          </span>
                        ) : regCodeCountdown > 0 ? (
                          `${regCodeCountdown}s`
                        ) : regCodeSent ? (
                          '重新发送'
                        ) : (
                          '获取验证码'
                        )}
                      </button>
                    </div>
                    {regCodeError && (
                      <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>{regCodeError}
                      </p>
                    )}
                    {!regCodeError && regCodeSent && (
                      <p className="mt-1.5 text-xs text-gray-400">
                        验证码已发送至 {regForm.email}，有效期 10 分钟
                      </p>
                    )}
                  </div>

                  <div>
                    <InputField
                      label="设置密码"
                      type={showRegPwd ? 'text' : 'password'}
                      value={regForm.password}
                      onChange={(v) => setRegForm({ ...regForm, password: v })}
                      onBlur={() => setRegTouched((p) => ({ ...p, password: true }))}
                      placeholder="至少 8 位，需含字母和数字"
                      error={regErrors.password}
                      required
                      rightSlot={
                        <button
                          type="button"
                          onClick={() => setShowRegPwd((v) => !v)}
                          className="cursor-pointer text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <i className={`${showRegPwd ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`}></i>
                        </button>
                      }
                    />
                    <PasswordStrengthBar password={regForm.password} />
                  </div>

                  <div>
                    <InputField
                      label="确认密码"
                      type={showConfirmPwd ? 'text' : 'password'}
                      value={regForm.confirm}
                      onChange={(v) => setRegForm({ ...regForm, confirm: v })}
                      onBlur={() => setRegTouched((p) => ({ ...p, confirm: true }))}
                      placeholder="再次输入密码"
                      error={regErrors.confirm}
                      success={regTouched.confirm && !regErrors.confirm && !!regForm.confirm && regForm.confirm === regForm.password}
                      required
                      rightSlot={
                        <button
                          type="button"
                          onClick={() => setShowConfirmPwd((v) => !v)}
                          className="cursor-pointer text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <i className={`${showConfirmPwd ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`}></i>
                        </button>
                      }
                    />
                  </div>

                  {/* Terms */}
                  <div>
                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <div
                        onClick={() => { setAgreeTerms((v) => !v); setAgreeError(''); }}
                        className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all cursor-pointer shrink-0 mt-0.5 ${
                          agreeTerms ? 'bg-[#0ea5e9] border-[#0ea5e9]' : agreeError ? 'border-red-400' : 'border-gray-300'
                        }`}
                      >
                        {agreeTerms && <i className="ri-check-line text-white text-xs"></i>}
                      </div>
                      <span className="text-sm text-gray-600 leading-relaxed">
                        我已阅读并同意
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setDocModal('terms'); }}
                          className="text-[#0ea5e9] hover:underline cursor-pointer mx-0.5"
                        >
                          《用户协议》
                        </button>
                        和
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setDocModal('privacy'); }}
                          className="text-[#0ea5e9] hover:underline cursor-pointer mx-0.5"
                        >
                          《隐私政策》
                        </button>
                      </span>
                    </label>
                    {agreeError && (
                      <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1 ml-6">
                        <i className="ri-error-warning-line"></i>{agreeError}
                      </p>
                    )}
                  </div>

                  {/* Password strength gate */}
                  {regForm.password && pwdStrength.score <= 1 && (
                    <div className="p-3 bg-amber-50 border border-amber-100 rounded-xl flex items-start gap-2">
                      <i className="ri-alert-line text-amber-500 text-sm mt-0.5 shrink-0"></i>
                      <p className="text-xs text-amber-700">密码强度较弱，建议使用大写字母、数字和特殊符号的组合</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={regLoading || regSuccess}
                    className="w-full py-3 bg-[#0ea5e9] text-white font-semibold rounded-xl hover:bg-[#0284c7] transition-all cursor-pointer whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {regLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        注册中...
                      </>
                    ) : regSuccess ? (
                      <>
                        <i className="ri-checkbox-circle-line"></i>
                        注册成功
                      </>
                    ) : '免费注册'}
                  </button>
                </form>

                <p className="text-center text-sm text-gray-500 mt-5">
                  已有账号？
                  <button onClick={() => switchTab('login')} className="text-[#0ea5e9] font-semibold cursor-pointer ml-1">
                    立即登录
                  </button>
                </p>
              </>
            )}
          </div>

          <div className="text-center mt-6">
            <Link to="/" className="text-sm text-gray-400 hover:text-gray-600 cursor-pointer inline-flex items-center gap-1">
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </div>
      </div>
    </div>
  </div>
  );
}
